import { deserialize } from 'deserialize-json-api'
import { camelCase, chain, head } from 'lodash'

import { TodoType, UserRole } from '@shared/utils'

export const fileConfig = { skipDeserialization: true, timeout: Infinity, responseType: 'blob' }

// For plain (non-JSON:API) responses that Service does not camelize automatically
export const deepCamelizeKeys = (value) => {
  if (Array.isArray(value)) return value.map(deepCamelizeKeys)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, nested]) => [camelCase(key), deepCamelizeKeys(nested)]))
  }
  return value
}

export const deserializeTodos = (data) => {
  return data.map((item) => {
    if (item.type === TodoType.HivSelfTest) {
      const { serializable_lab: serializableLab, ...rest } = item
      return {
        ...rest,
        lab: deserialize(serializableLab, { transformKeys: 'camelCase' })?.data ?? {},
      }
    }
    if (item.type === TodoType.RescheduleAppointment) {
      const { serializable_appointment: serializableAppointment, ...rest } = item
      return {
        ...rest,
        appointment: deserialize(serializableAppointment, { transformKeys: 'camelCase' })?.data ?? {},
      }
    }
    if (item.type === TodoType.Insurance) {
      const { serializable_pharmacy: data, ...rest } = item
      const pharmacy = deserialize(data, { transformKeys: 'camelCase' })?.data ?? {}
      return { ...rest, pharmacy }
    }
    if (item.type === TodoType.Generic) {
      const { serializable_todo: data, ...rest } = item
      const todo = deserialize(data, { transformKeys: 'camelCase' })?.data ?? {}
      return { ...rest, todo }
    }
    return item
  })
}

export const deserializeEmbeddedSoapNote = (data) => {
  if (data.soap_note && 'string' === typeof data.soap_note && data.soap_note.length) {
    data.soap_note = JSON.parse(data.soap_note)
  } else {
    data.soap_note = {}
  }

  return data
}

export const mapRuleSets = (data) => ({
  executiveUserId: data?.executiveUserId,
  grant340BInformation: JSON.parse(data?.grant340BInformation ?? '{}'),
  additionalServices: JSON.parse(data?.additionalServices ?? '{}'),
  patientCare: JSON.parse(data?.patientCare ?? '{}'),
  financials: JSON.parse(data?.financials ?? '{}'),
  contacts: JSON.parse(data?.contacts ?? '[]'),
  prescriptionPartners: JSON.parse(data?.prescriptionPartners ?? '[]'),
  lhcDetails: JSON.parse(data?.lhcDetails ?? '{}'),
  updatedAt: data.updatedAt,
})

export const mapDashboardLabs = (data) => ({
  review: deserialize(data.review_status, { transformKeys: 'camelCase' })?.data ?? [],
  followUp: deserialize(data.follow_up_status, { transformKeys: 'camelCase' })?.data ?? [],
  positive: deserialize(data.positive_status, { transformKeys: 'camelCase' })?.data ?? [],
})

export const mapQuarterlyAutomations = (response = {}) => {
  const flatten = (resources, recordKind) =>
    (deserialize({ data: resources ?? [] }, { transformKeys: 'camelCase' })?.data ?? []).map((record) => ({
      ...record,
      recordKind,
      // created_by comes through as a raw user object without a fullName; add it so the User cell can render it.
      createdBy: record.createdBy && {
        ...record.createdBy,
        fullName: [record.createdBy.firstName, record.createdBy.lastName].filter(Boolean).join(' '),
      },
    }))

  const records = [...flatten(response.lab_requests, 'labRequest'), ...flatten(response.assessments, 'assessment')].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  )

  const meta = response.meta ?? {}

  return {
    records,
    meta: {
      patientId: meta.patient_id,
      canCreateNewFlow: Boolean(meta.can_create_new_flow),
      canCreateNewQha: Boolean(meta.can_create_new_qha),
      canCreateNewHivFlow: Boolean(meta.can_create_new_hiv_flow),
    },
  }
}

export const mapGaps = (data) => {
  const appointments = Object.entries(data.appointments).reduce((acc, [key, value]) => {
    acc[key] = deserialize(value, { transformKeys: 'camelCase' })?.data ?? []
    return acc
  }, {})

  const availabilities = Object.entries(data.availabilities).reduce((acc, [key, value]) => {
    acc[key] = deserialize(value, { transformKeys: 'camelCase' })?.data ?? []
    return acc
  }, {})

  return {
    timezone: data.timezone,
    appointments,
    availabilities,
    providers: deserialize(data.providers, { transformKeys: 'camelCase' })?.data ?? [],
    gaps: deserialize(data.gaps, { transformKeys: 'camelCase' })?.data ?? [],
  }
}

export const mapWeeklyGaps = (data) => ({
  timezone: data.timezone,
  gaps: deserialize(data.gaps, { transformKeys: 'camelCase' })?.data ?? [],
  providerTypes: deserialize(data.provider_types, { transformKeys: 'camelCase' })?.data ?? [],
  appointmentTypes: data.appointment_types || [],
})

export const mapUserCard = (data) => ({
  id: data.id,
  fullName: data.fullName,
  phone: data.phone,
  profilePictureThumbUrl: data.profilePictureThumbUrl,
  role: data.role,
  pronouns: data.pronouns,
  timezone: data.timezone,
  outOfOfficeMessage: data.outOfOfficeMessage,
  outOfOffice: data.outOfOffice,
  userSettings: data.userSettings,
  disabled: data.disabled,
  accessible: data.accessible,
  ...(data.role !== UserRole.Patient && {
    lastLogin: data.lastLogin,
  }),
  ...(data.role === UserRole.Provider && {
    fullName: data.providerFullName,
    provider: {
      id: data.providerId,
      npi: data.providerNpi,
      providerTypes: data.providerTypes,
      licenses: data.licenses,
      status: data.providerStatus,
      homeAddress: {
        city: data.providerCity,
        state: data.providerStateAbbreviation,
      },
    },
  }),
  ...(data.role === UserRole.CBO && {
    cbo: {
      name: data.cboName,
    },
  }),
  ...([UserRole.Pharmacy, UserRole.PharmacyManager].includes(data.role) && {
    pharmacy: {
      name: data.pharmacyName,
    },
  }),
  ...(data.role === UserRole.Patient && {
    enrolled: data.enrolled,
    consent: data.consent,
    cashPay: data.cashPay,
    patient: {
      treatmentTypes: data.treatmentTypes,
      homeAddress: {
        city: data.homeAddressCity,
        state: data.homeAddressState,
      },
      ...(data.providerFullName && {
        provider: {
          fullName: data.providerFullName,
          profilePictureThumbUrl: data.providerProfilePictureThumbUrl,
        },
      }),
      cbo: {
        name: data.cboName,
      },
      pharmacy: {
        name: data.pharmacyName,
      },
    },
  }),
})

export const mapSettings = (data) => {
  if (data.length === 0) return undefined
  return data.reduce((acc, setting) => ({ ...acc, [setting.key]: setting.value }), {})
}

export const mapTours = (data) => chain(data).groupBy('name').mapValues(head).value()

export const mapTaskFiles = (data = []) =>
  data.map((taskFile) => ({
    ...taskFile,
    file: taskFile.file ?? {
      filename: taskFile.filename,
      mimetype: taskFile.mimetype,
      url: taskFile.url,
      createdAt: taskFile.createdAt,
    },
  }))
